import { extractDataAxios, extractErrorMessageAxios } from "@/httpClient";
import { createNamespacedHelpers } from "vuex";

const initState = {
  data: [],
  vehicleNotes: null,
  isLoading: false,
  error: null,
  suggestionConverterCodes: [],
  isConvertersCodesLoading: false,
  suggestionConverterCodesError: null,
  searchCatalyticConverterCodesTimeoutToken: null,
  selectedCode: null,
  allManufacturers: [],
  selectedManufacturer: null,
  isManufacturersLoading: false,
  manufacturersError: null,
  allYears: [],
  selectedYear: null,
  isYearsLoading: false,
  yearsError: null,
  makes: [],
  selectedMake: null,
  isMakesLoading: false,
  makesError: null,
  models: [],
  selectedModel: null,
  isModelsLoading: false,
  modelsError: null,
  engineSizes: [],
  selectedEngineSize: null,
  isEngineSizesLoading: false,
  engineSizesError: null,
  searchBy: "SEARCH_BY_CODE_OR_GRADE_NAME",
};

const searchCatalyticConverters = {
  namespaced: true,
  state: () => initState,

  mutations: {
    FETCH_BEGIN(state) {
      state.isLoading = true;
      state.error = null;
    },

    FETCH_SUCCESS(state, payload) {
      state.isLoading = false;
      state.data = payload;
    },

    FETCH_ERROR(state, error) {
      state.isLoading = false;
      state.error = error;
    },
    CLEAR(state) {
      state.data = [];
      state.vehicleNotes = null;
      state.selectedCode = null;
      state.selectedManufacturer = null;
      state.selectedMake = null;
      state.selectedModel = null;
      state.selectedEngineSize = null;
    },
    FETCH_CONVERTER_CODES_BEGIN(state, timeoutToken) {
      state.suggestionConvertersCodes = [];
      state.isConvertersCodesLoading = true;
      if (state.searchCatalyticConverterCodesTimeoutToken) {
        clearTimeout(state.searchCatalyticConverterCodesTimeoutToken);
        state.searchCatalyticConverterCodesTimeoutToken = timeoutToken;
      }
      state.suggestionConverterCodesError = null;
    },
    SET_SEARCH_CATALYTIC_CONVERTER_CODES_TIMEOUT_TOKEN(state, timeoutToken) {
      state.searchCatalyticConverterCodesTimeoutToken = timeoutToken;
    },
    FETCH_CONVERTER_CODES_SUCCESS(state, payload) {
      state.suggestionConverterCodes = payload;
      state.isConvertersCodesLoading = false;
    },
    SET_SELECTED_CODE(state, code) {
      state.data = [];
      state.selectedCode = code;
    },
    FETCH_CONVERTER_CODES_ERROR(state, error) {
      state.isConvertersCodesLoading = false;
      state.suggestionConverterCodesError = error;
    },
    FETCH_ALL_MANUFACTURERS_BEGIN(state) {
      state.isManufacturersLoading = true;
      state.manufacturersError = null;
    },
    FETCH_ALL_MANUFACTURERS_SUCCESS(state, payload) {
      state.isManufacturersLoading = false;
      state.allManufacturers = payload;
    },
    FETCH_ALL_MANUFACTURERS_ERROR(state, error) {
      state.manufacturersError = error;
      state.isManufacturersLoading = false;
    },
    SET_SELECTED_MANUFACTURER(state, manufacturer) {
      state.data = [];
      state.selectedManufacturer = manufacturer;
    },
    FETCH_ALL_YEARS_BEGIN(state) {
      state.isYearsLoading = true;
      state.yearsError = null;
    },
    FETCH_ALL_YEARS_SUCCESS(state, payload) {
      state.isYearsLoading = false;
      state.allYears = payload;
    },
    FETCH_ALL_YEARS_ERROR(state, error) {
      state.yearsError = error;
      state.isYearsLoading = false;
    },
    SET_SELECTED_YEAR(state, year) {
      state.data = [];
      state.selectedYear = year;
      state.selectedMake = null;
      state.selectedModel = null;
      state.selectedEngineSize = null;
    },
    FETCH_MAKES_BEGIN(state) {
      state.makesError = null;
      state.isMakesLoading = true
    },
    FETCH_MAKES_SUCCESS(state, payload) {
      state.isMakesLoading = false;
      state.makes = payload
    },
    FETCH_MAKES_ERROR(state, error) {
      state.makesError = error;
      state.isMakesLoading = false;
    },
    SET_SELECTED_MAKE(state, make) {
      state.data = [];
      state.selectedMake = make;
      state.selectedModel = null;
      state.selectedEngineSize = null;
    },
    FETCH_MODELS_BEGIN(state) {
      state.modelsError = null;
      state.isModelsLoading = true
    },
    FETCH_MODELS_SUCCESS(state, payload) {
      state.isModelsLoading = false;
      state.models = payload
    },
    FETCH_MODELS_ERROR(state, error) {
      state.modelsError = error;
      state.isModelsLoading = false;
    },
    SET_SELECTED_MODEL(state, model) {
      state.data = [];
      state.selectedModel = model;
      state.selectedEngineSize = null;
    },
    FETCH_ENGINE_SIZES_BEGIN(state) {
      state.engineSizesError = null;
      state.isEngineSizesLoading = true;
    },
    FETCH_ENGINE_SIZES_SUCCESS(state, payload) {
      state.isEngineSizesLoading = false;
      state.engineSizes = payload;
    },
    FETCH_ENGINE_SIZES_ERROR(state, error) {
      state.engineSizesError = error;
      state.isEngineSizesLoading = false;
    },
    SET_SELECTED_ENGINE_SIZE(state, engineSize) {
      state.data = [];
      state.selectedEngineSize = engineSize;
    },
    SET_VEHICLE_NOTES(state, vehicleNotes) {
      state.vehicleNotes = vehicleNotes;
    },
    SET_SEARCH_BY(state, searchBy) {
      state.searchBy = searchBy;
    }
  },

  actions: {
    queryConverterCodes({ commit }, codeTerm) {
      commit('FETCH_CONVERTER_CODES_BEGIN', setTimeout(async () => {
        try {
          const data = extractDataAxios(
            await window.axios.get("/api/catalytic-converters/get-code-suggestions/" + codeTerm)
          );
          commit('FETCH_CONVERTER_CODES_SUCCESS', data);
        } catch (e) {
          console.error(e);
          commit('FETCH_CONVERTER_CODES_ERROR', extractErrorMessageAxios(e));
        }
      }, 1500));
    },

    async setSearchBy({ commit }, searchBy) {
      // clear searched converters
      commit('CLEAR');
      commit('SET_SEARCH_BY', searchBy);
      if (searchBy === "FILTER_BY_MANUFACTURER") {
        commit("FETCH_ALL_MANUFACTURERS_BEGIN");
        try {
          const data = extractDataAxios(await window.axios.get("/api/catalytic-converters/list-all-manufacturers"));
          commit("FETCH_ALL_MANUFACTURERS_SUCCESS", data);
        } catch (e) {
          console.error(e);
          commit('FETCH_ALL_MANUFACTURERS_ERROR', extractErrorMessageAxios(e));
        }
      }
      if (searchBy === "FILTER_BY_VEHICLE") {
        commit("FETCH_ALL_YEARS_BEGIN");
        try {
          const data = extractDataAxios(await window.axios.get("/api/vehicles/list-all-years"));
          commit("FETCH_ALL_YEARS_SUCCESS", data);
        } catch (e) {
          console.error(e);
          commit('FETCH_ALL_YEARS_ERROR', extractErrorMessageAxios(e));
        }
      }
    },

    async fetchByCode({ commit }, codeOrGradeName) {
      commit('SET_SELECTED_CODE', codeOrGradeName);
      if (codeOrGradeName) {
        commit('FETCH_BEGIN');
          try {
            const data = extractDataAxios(await window.axios.get(`/api/catalytic-converters/get-by-code/${codeOrGradeName}`));
            commit('FETCH_SUCCESS', data);
          }
          catch (e) {
            console.error(e);
            commit('FETCH_ERROR', extractErrorMessageAxios(e));
          }
      }
    },
    async fetchByManufacturer({ commit }, manufacturer) {
      commit('SET_SELECTED_MANUFACTURER', manufacturer);
      commit('FETCH_BEGIN');
      try {
        const data = extractDataAxios(await window.axios.get(`/api/catalytic-converters/get-by-manufacturer/${manufacturer}`));
        commit('FETCH_SUCCESS', data);
      }
      catch (e) {
        console.error(e);
        commit('FETCH_ERROR', extractErrorMessageAxios(e));
      }
    },
    async selectYear({ commit }, year) {
      commit('SET_SELECTED_YEAR', year);

      if (year) {
        commit('FETCH_MAKES_BEGIN');
        // fetch make next to show
        try {
          const data = extractDataAxios(await window.axios.get(`/api/vehicles/get-makes/${year}`));
          commit('FETCH_MAKES_SUCCESS', data);
        }
        catch (e) {
          console.error(e);
          commit('FETCH_MAKES_ERROR', extractErrorMessageAxios(e));
        }
      }
    },

    async selectMake({ commit, state }, make) {
      commit('SET_SELECTED_MAKE', make);

      if (make) {
        commit('FETCH_MODELS_BEGIN');
        try {
          const data = extractDataAxios(await window.axios.get(`/api/vehicles/get-models/${state.selectedYear}/${make}`));
          commit('FETCH_MODELS_SUCCESS', data);
        }
        catch (e) {
          console.error(e);
          commit('FETCH_MODELS_ERROR', extractErrorMessageAxios(e));
        }
      }
    },

    async selectModel({ commit, state }, model) {
      commit('SET_SELECTED_MODEL', model);
      if (model) {
        commit('FETCH_ENGINE_SIZES_BEGIN');
        try {
          const data = extractDataAxios(await window.axios.get(`/api/vehicles/get-engine-sizes/${state.selectedYear}/${state.selectedMake}/${model}`));
          commit('FETCH_ENGINE_SIZES_SUCCESS', data);
        }
        catch (e) {
          console.error(e);
          commit('FETCH_ENGINE_SIZES_ERROR', extractErrorMessageAxios(e));
        }
      }
    },

    async selectEngineSize({ commit, state }, engineSize) {
      commit('SET_SELECTED_ENGINE_SIZE', engineSize);
      commit('SET_VEHICLE_NOTES', null);
      if (engineSize) {
        commit('FETCH_BEGIN');
        try {
          const data = extractDataAxios(await window.axios.get(`/api/catalytic-converters/get-by-vehicle/${state.selectedYear}/${state.selectedMake}/${state.selectedModel}/${engineSize}`));
          commit('FETCH_SUCCESS', data.catalytic_converters.data);
          commit('SET_VEHICLE_NOTES', data.vehicle_notes);
        }
        catch (e) {
          console.error(e);
          commit('FETCH_ERROR', extractErrorMessageAxios(e));
        }
      }
    }
  },
}

const { mapState, mapActions, mapMutations } = createNamespacedHelpers('searchCatalyticConverters');

export { mapState, mapActions, mapMutations };

export default searchCatalyticConverters;